import { useQuery } from '@apollo/client';
import {
  ActionButton,
  IDropdownOption,
  Separator,
  Stack,
  Text,
} from '@fluentui/react';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { WORKGROUP_CURRENCY_VALUES } from '../../../constants';
import { CorporateWorkgroupValues } from '../../../types';
import {
  AvailableWorkgroupCurrencies,
  AvailableWorkgroupCurrenciesVariables,
} from './__generated__/AvailableWorkgroupCurrencies';
import { useColumns } from './column.data';
import { ColumnsHeader } from './ColumnHeader';
import { FormField } from './FormField';

import AVAILABLE_WORKGROUPS_CURRENCIES from './AvailableWorkgroupCurrencies.graphql';
interface CurrencySectionProps {
  workgroupId: string | undefined;
  businessUnitId: string | undefined;
}
export const CurrencySection: React.FC<CurrencySectionProps> = ({
  workgroupId,
  businessUnitId,
}) => {
  const { columnArray } = useColumns();
  const { control } = useFormContext<CorporateWorkgroupValues>();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'workgroupCurrencies',
  });

  const { data: currencyList } = useQuery<
    AvailableWorkgroupCurrencies,
    AvailableWorkgroupCurrenciesVariables
  >(AVAILABLE_WORKGROUPS_CURRENCIES, {
    variables: {
      businessUnitId: businessUnitId!,
      workgroupId: workgroupId ?? null,
    },
    skip: !businessUnitId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  return (
    <Stack
      tokens={{
        padding: '10px 25px 0px 25px',
      }}
      style={{ marginTop: 10 }}
    >
      <Text variant="xLarge" style={{ marginBottom: 20, marginTop: 20 }}>
        Exchange Rates
      </Text>
      <Stack horizontal tokens={{ childrenGap: 20 }}>
        <ColumnsHeader columnsArray={columnArray} />
      </Stack>
      <Separator />
      <Stack tokens={{ childrenGap: 20 }}>
        {fields.map((field, index) => {
          const baseField = `workgroupCurrencies.${index}`;
          const currencyOptions: IDropdownOption[] =
            currencyList?.availableWorkgroupCurrencies?.nodes
              .filter(
                (item) =>
                  // Keep the option if its ID matches one from the `fields` array, or it's not already in `fields`
                  item.id === field.currencyId ||
                  !fields?.some((field) => field.currencyId === item.id)
              )
              .map((item) => ({
                key: item.id,
                text: item?.isoCode ?? 'NA',
                exchangeRate: item?.companyExchangeRate,
                spotRate: item?.companySpotRate,
                isSpotRatePreferred: false,
              })) || [];
          return (
            <Stack
              key={index}
              horizontal
              tokens={{ childrenGap: 30 }}
              style={{ marginLeft: 20 }}
              verticalAlign="center"
            >
              {columnArray.map((value, key) => {
                if (value.isVisible)
                  return (
                    <FormField
                      baseField={baseField}
                      columnData={value}
                      key={field.id + key}
                      remove={remove}
                      index={index}
                      currencyOptions={currencyOptions}
                      currencyDisabled={!!field.id && !!field._rowTimestamp}
                    />
                  );
                else return null;
              })}
            </Stack>
          );
        })}
        <Stack horizontalAlign="start">
          <ActionButton
            iconProps={{ iconName: 'add' }}
            disabled={!businessUnitId}
            text="Add"
            onClick={() => {
              append(WORKGROUP_CURRENCY_VALUES, { shouldFocus: false });
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
